/**
 * i18n Translation Service for Frontend
 * Loads and manages translations from JSON files
 */

import vnTranslations from './locales/vn.json';
import gbTranslations from './locales/gb.json';
import cnTranslations from './locales/cn.json';
import jpTranslations from './locales/jp.json';
import krTranslations from './locales/kr.json';

export type LanguageCode = 'vn' | 'gb' | 'cn' | 'jp' | 'kr';

/** Ngôn ngữ mặc định của ứng dụng */
export const DEFAULT_LANGUAGE: LanguageCode = 'vn';

export const LANGUAGE_STORAGE_KEY = 'co_space_language';
const LEGACY_LANGUAGE_STORAGE_KEY = 'language';

const HTML_LANG_MAP: Record<LanguageCode, string> = {
  vn: 'vi',
  gb: 'en',
  cn: 'zh',
  jp: 'ja',
  kr: 'ko',
};

export interface Translations {
  [key: string]: any;
}

const translations: Record<LanguageCode, Translations> = {
  vn: vnTranslations,
  gb: gbTranslations,
  cn: cnTranslations,
  jp: jpTranslations,
  kr: krTranslations,
};

export const supportedLanguages: Record<LanguageCode, string> = {
  vn: 'Tiếng Việt',
  gb: 'English',
  cn: '中文',
  jp: '日本語',
  kr: '한국어',
};

/**
 * Get translation by key path (e.g., 'auth.login.title')
 * Supports nested keys with dot notation
 * Supports parameter substitution with {paramName} syntax
 */
export function t(key: string, lang: LanguageCode = 'vn', params?: Record<string, string | number>): string {
  const translation = translations[lang] || translations.vn;
  
  // Navigate through nested object
  const keys = key.split('.');
  let value: any = translation;
  
  for (const k of keys) {
    if (value && typeof value === 'object' && k in value) {
      value = value[k];
    } else {
      // Key not found, return the key itself
      return key;
    }
  }
  
  // If value is a string, format it with params if provided
  if (typeof value === 'string') {
    if (params) {
      return value.replace(/\{\{?(\w+)\}?\}/g, (match, paramKey) => {
        return params[paramKey]?.toString() || match;
      });
    }
    return value;
  }
  
  // If value is not a string, return the key
  return key;
}

/**
 * Get all translations for a language
 */
export function getTranslations(lang: LanguageCode = 'vn'): Translations {
  return translations[lang] || translations.vn;
}

/**
 * Get supported languages
 */
export function getSupportedLanguages(): Record<LanguageCode, string> {
  return supportedLanguages;
}

/**
 * Check if a language code is supported
 */
export function isLanguageSupported(lang: string): lang is LanguageCode {
  return lang in supportedLanguages;
}

export function normalizeLanguageCode(value: string | null | undefined): LanguageCode | null {
  if (!value) return null;

  const aliases: Record<string, LanguageCode> = {
    vi: 'vn',
    en: 'gb',
    zh: 'cn',
    ja: 'jp',
    ko: 'kr',
  };

  const normalized = aliases[value] || value;
  return isLanguageSupported(normalized) ? normalized : null;
}

export function applyDocumentLanguage(lang: LanguageCode): void {
  if (typeof document === 'undefined') return;
  document.documentElement.lang = HTML_LANG_MAP[lang] || lang;
}

/**
 * Get the saved language, falling back to Vietnamese for new sessions.
 */
export function getDefaultLanguage(): LanguageCode {
  if (typeof window === 'undefined') {
    return DEFAULT_LANGUAGE;
  }

  const stored =
    localStorage.getItem(LANGUAGE_STORAGE_KEY) ||
    localStorage.getItem(LEGACY_LANGUAGE_STORAGE_KEY);
  const normalized = normalizeLanguageCode(stored);
  if (normalized) {
    return normalized;
  }

  setLanguage(DEFAULT_LANGUAGE);
  return DEFAULT_LANGUAGE;
}

/**
 * Set language preference
 */
export function setLanguage(lang: LanguageCode): void {
  localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
  localStorage.setItem(LEGACY_LANGUAGE_STORAGE_KEY, lang);
  applyDocumentLanguage(lang);
}

